import { getToken } from '../utils/authToken';
import { includeInCsp } from '../utils/HeaderContext';
import gtag from './gtag';

const ANALYTICS_ID_PROD = 'G-DEYJBVMZRC';
const ANALYTICS_ID_DEV = 'G-G5R4J3MCMY';

export interface Props {
    config?: object;
}

export default function GA4({ config = {} }: Props) {
    includeInCsp('script-src', 'https://www.googletagmanager.com/');
    includeInCsp('connect-src', 'https://*.google-analytics.com/');
    return (
        <>
            <script
                id="ga4-script"
                type="application/json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({ ...config, currency: 'NOK' }),
                }}
            ></script>
        </>
    );
}

export function loadGoogleAnalytics(dataLayer?: []) {
    // clear the dataLayer if this method is called from the dialog
    window.dataLayer = dataLayer || window.dataLayer;
    const ANALYTICS_ID = location.host === 'www.vinmonopolet.no' ? ANALYTICS_ID_PROD : ANALYTICS_ID_DEV;
    gtag('js', new Date());
    try {
        const user = getToken()?.user;
        const config = JSON.parse(document.getElementById('ga4-script')?.textContent ?? '{}');
        if (user) {
            gtag('config', ANALYTICS_ID, {
                ...config,
                user_id: user.trackingId,
                user_properties: {
                    store: user.favouriteStore?.displayName,
                    postcode: user.defaultAddress?.postalCode,
                },
            });
        } else {
            gtag('config', ANALYTICS_ID, config);
        }
    } catch {
        gtag('config', ANALYTICS_ID, {});
    }

    addEventListener('error', function (event) {
        gtag('event', 'exception', {
            description: event.message,
            stack: event.error instanceof Error ? event.error.stack : event.message,
        });
    });

    const scriptElm = document.createElement('script');
    scriptElm.src = `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_ID}`;
    scriptElm.async = true;
    document.head.appendChild(scriptElm);
}

/**
 * Track all button and link clicks inside elements with the data-track-link-click attribute
 * This is primarily used to track links on frontpage and category page
 */
export function trackLinkClicks() {
    document.addEventListener(
        'click',
        ({ target }) => {
            if (target instanceof HTMLElement) {
                const buttonOrLink = target.closest('a, button');
                const module = buttonOrLink?.closest<HTMLElement>('[data-track-link-click]')?.dataset.trackLinkClick;
                if (buttonOrLink && module) {
                    gtag('event', 'link_click', {
                        link_text: buttonOrLink.textContent,
                        link_url: buttonOrLink instanceof HTMLAnchorElement ? buttonOrLink.href : undefined,
                        module,
                    });
                }
            }
        },
        { capture: true },
    );
}

/**
 * Track toggeling of <details data-toggle-context="something"> elements.
 * The link_text is either the text in `details summary h2` or, if that is missing, `details summary`.
 * The context is read from the data-toggle-context attribute.
 */
export function trackDetailsToggle() {
    document.addEventListener(
        'toggle',
        ({ target }) => {
            if (target instanceof HTMLDetailsElement) {
                const context = target.getAttribute('data-toggle-context');
                const summary = target.querySelector('summary');
                const h2 = summary?.querySelector('h2');
                const link_text = h2?.textContent ?? summary?.textContent;
                if (context && link_text) {
                    gtag('event', 'expandable', {
                        context,
                        link_text,
                    });
                }
            }
        },
        { capture: true },
    );
}
